import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { api } from "@/lib/firebase/api-client";
import { COLLECTIONS } from "@/lib/constants";
import {
  startOrderAlarm,
  stopOrderAlarm,
  unlockOrderAlarm,
  sonAutorise,
  testerOrderAlarm,
} from "@/lib/utils/order-alarm";

type CommandeArrivee = {
  id: string;
  code: string;
};

/**
 * Alerte sonore et visuelle a l'arrivee d'une commande.
 *
 * A placer dans une mise en page (AdminLayout, SupportLayout) pour que
 * l'alerte fonctionne quelle que soit la page ouverte.
 *
 * Seules les commandes creees APRES l'ouverture de la page declenchent
 * l'alarme : aucune sonnerie au chargement pour l'historique.
 */
export default function NewOrderAlarm({
  lienCommandes = "/admin/orders",
  storeId,
  attendreStore = false,
  parApi = false,
  sansBoutonTest = false,
}: {
  lienCommandes?: string;
  /**
   * Sans le petit bouton « Tester le son » en bas a droite (20/10/2026) :
   * sur le telephone du responsable commande, il couvrait la barre du bas
   * et empechait de se deconnecter. Le bandeau « son bloque » reste.
   */
  sansBoutonTest?: boolean;
  /**
   * Quand il est fourni, seules les commandes de ce magasin declenchent
   * l'alarme. C'est indispensable pour un collaborateur : les regles
   * Firestore ne lui laissent lire que son magasin, et une requete qui
   * depasse ce perimetre est refusee ENTIEREMENT — pas filtree.
   */
  storeId?: string | null;
  /**
   * Vrai quand l'ecoute n'a de sens qu'avec un magasin connu. Tant qu'il
   * n'est pas la, on n'ecoute pas : une requete refusee ne sonne pas, et un
   * silence pris pour « aucune commande » est pire que pas d'alarme du tout.
   */
  attendreStore?: boolean;
  /**
   * Vrai pour le responsable commande (04/10/2026) : l'alarme interroge le
   * serveur toutes les 15 secondes au lieu de lire Firestore. Les regles
   * ne lui ouvrent plus les fiches de commande, qui contiennent le
   * telephone et l'adresse du client.
   */
  parApi?: boolean;
}) {
  const [arrivees, setArrivees] = useState<CommandeArrivee[]>([]);
  const [sonPret, setSonPret] = useState(false);

  // Les navigateurs refusent de jouer un son avant toute interaction :
  // on deverrouille au premier clic ou a la premiere touche.
  useEffect(() => {
    const debloquer = () => {
      unlockOrderAlarm();
      // Le deblocage n'est pas instantane : on relit l'etat juste apres.
      window.setTimeout(() => setSonPret(sonAutorise()), 150);
    };
    window.addEventListener("pointerdown", debloquer);
    window.addEventListener("keydown", debloquer);

    // Certains navigateurs autorisent le son d'emblee : on verifie regulierement
    // plutot que de supposer. Tant que ce n'est pas le cas, on le dit a l'ecran.
    const minuterie = window.setInterval(() => setSonPret(sonAutorise()), 3000);

    return () => {
      window.removeEventListener("pointerdown", debloquer);
      window.removeEventListener("keydown", debloquer);
      clearInterval(minuterie);
    };
  }, []);

  // L'alarme par le serveur (responsable commande).
  useEffect(() => {
    if (!parApi) return;
    if (attendreStore && !storeId) return;

    const debut = new Date().toISOString();
    const connus = new Set<string>();
    let premier = true;
    let actif = true;

    const relever = async () => {
      try {
        const r = await api.orders.arrivees(debut, storeId);
        if (!actif) return;
        const nouvelles = (r.commandes || []).filter((c) => !connus.has(c.id));
        nouvelles.forEach((c) => connus.add(c.id));
        // Le premier releve ne fait que noter l'existant.
        if (premier) {
          premier = false;
          if (nouvelles.length === 0) return;
        }
        if (nouvelles.length === 0) return;
        setArrivees((prev) => {
          const deja = new Set(prev.map((o) => o.id));
          const ajouts = nouvelles.filter((o) => !deja.has(o.id));
          if (ajouts.length === 0) return prev;
          startOrderAlarm();
          return [...prev, ...ajouts];
        });
      } catch (err: any) {
        // Une erreur de reseau ne doit jamais casser la page.
        console.error("[ALARME COMMANDES] Releve impossible :", err?.message);
      }
    };

    relever();
    const minuterie = window.setInterval(relever, 15000);
    return () => {
      actif = false;
      clearInterval(minuterie);
      stopOrderAlarm();
    };
  }, [parApi, storeId, attendreStore]);

  useEffect(() => {
    if (parApi) return;
    if (attendreStore && !storeId) return;

    const depuis = Timestamp.fromDate(new Date());

    // L'index (storeId + createdAt) existe deja : cette requete ne demande
    // aucune creation d'index.
    const q = storeId
      ? query(
          collection(db, COLLECTIONS.ORDERS),
          where("storeId", "==", storeId),
          where("createdAt", ">=", depuis),
          orderBy("createdAt", "desc"),
        )
      : query(
          collection(db, COLLECTIONS.ORDERS),
          where("createdAt", ">=", depuis),
          orderBy("createdAt", "desc"),
        );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nouvelles = snapshot
          .docChanges()
          .filter((c) => c.type === "added")
          .map((c) => {
            const d: any = c.doc.data();
            return {
              id: c.doc.id,
              code: String(d.orderCode || d.externalId || c.doc.id).slice(0, 20),
            };
          });

        if (nouvelles.length === 0) return;

        setArrivees((prev) => {
          const connus = new Set(prev.map((o) => o.id));
          const ajouts = nouvelles.filter((o) => !connus.has(o.id));
          if (ajouts.length === 0) return prev;
          startOrderAlarm();
          return [...prev, ...ajouts];
        });
      },
      (err) => {
        // Une erreur de droits ou de reseau ne doit jamais casser la page.
        console.error("[ALARME COMMANDES] Ecoute interrompue :", err?.message);
      },
    );

    return () => {
      unsubscribe();
      stopOrderAlarm();
    };
  }, [parApi, storeId, attendreStore]);

  const acquitter = () => {
    stopOrderAlarm();
    setArrivees([]);
  };

  // Aucune commande en attente : on affiche seulement l'etat du son.
  // Un bandeau orange tant que le navigateur bloque, puis un petit bouton
  // de test discret. Personne ne doit decouvrir a 11h que le son etait coupe.
  if (arrivees.length === 0) {
    if (!sonPret) {
      return (
        <div className="fixed inset-x-0 top-0 z-[10000] px-3 pt-3">
          <button
            type="button"
            onClick={() => {
              testerOrderAlarm();
              window.setTimeout(() => setSonPret(sonAutorise()), 200);
            }}
            className="mx-auto flex w-full max-w-3xl items-center justify-center gap-2 rounded-2xl border-2 border-amber-500 bg-amber-100 px-4 py-3 text-sm font-bold text-amber-900 shadow-lg hover:bg-amber-200"
          >
            🔇 Le son est bloqué par le navigateur — cliquez ici pour activer
            l'alerte des commandes
          </button>
        </div>
      );
    }

    if (sansBoutonTest) return null;
    return (
      <button
        type="button"
        onClick={() => testerOrderAlarm()}
        title="Jouer le son pour vérifier qu'il fonctionne"
        className="fixed bottom-3 right-3 z-[9999] rounded-full border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-600 shadow hover:bg-gray-50"
      >
        🔔 Tester le son
      </button>
    );
  }

  return (
    <div className="fixed inset-x-0 top-0 z-[10000] px-3 pt-3">
      <div className="mx-auto max-w-3xl rounded-2xl border-2 border-red-500 bg-red-600 text-white shadow-2xl">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-lg font-extrabold leading-tight">
              {arrivees.length === 1
                ? "NOUVELLE COMMANDE"
                : `${arrivees.length} NOUVELLES COMMANDES`}
            </p>
            <p className="mt-0.5 truncate text-sm text-red-100">
              {arrivees
                .slice(-3)
                .map((o) => o.code)
                .join("  ·  ")}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Link
              to={lienCommandes}
              onClick={acquitter}
              className="rounded-xl bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50"
            >
              Voir les commandes
            </Link>
            <button
              type="button"
              onClick={acquitter}
              className="rounded-xl border-2 border-white/70 px-4 py-2 text-sm font-bold text-white hover:bg-white/10"
            >
              Arrêter le son
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
